import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Minus, Save, RefreshCw, ArrowRightLeft, Warehouse, ChevronDown, ChevronUp, Edit2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { DarkStore, InventoryItem, InventoryTransaction, InventorySummaryItem, Product, Make, Model } from '../types';

// ── Searchable dropdown ───────────────────────────────────────────────────────
interface SearchableSelectOption { id: number; name: string; }
interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: number | '';
  onChange: (id: number | '') => void;
  placeholder: string;
  disabled?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({ options, value, onChange, placeholder, disabled }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.id === value);
  const filtered = query ? options.filter(o => o.name.toLowerCase().includes(query.toLowerCase())) : options;

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (opt: SearchableSelectOption | null) => {
    onChange(opt ? opt.id : '');
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <div
        onClick={() => !disabled && setOpen(o => !o)}
        className={`flex items-center justify-between border rounded-lg px-3 py-2 text-sm bg-white cursor-pointer min-w-[160px] focus-within:ring-2 focus-within:ring-emerald-500 ${
          disabled ? 'bg-gray-50 text-gray-400 cursor-not-allowed border-gray-200' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>
          {selected ? selected.name : placeholder}
        </span>
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          {selected && !disabled && (
            <button
              type="button"
              onClick={e => { e.stopPropagation(); select(null); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={12} />
            </button>
          )}
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                autoFocus
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Type to search..."
                className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            </div>
          </div>
          <div className="max-h-52 overflow-y-auto">
            <button
              type="button"
              onClick={() => select(null)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 ${!value ? 'text-emerald-600 font-medium bg-emerald-50' : 'text-gray-500'}`}
            >
              {placeholder}
            </button>
            {filtered.length === 0
              ? <div className="px-3 py-2 text-sm text-gray-400">No results</div>
              : filtered.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => select(opt)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 ${value === opt.id ? 'text-emerald-600 font-medium bg-emerald-50' : 'text-gray-900'}`}
                >
                  {opt.name}
                </button>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

const TABS = ['Stock Levels', 'Transfer', 'Transactions', 'Dark Stores'] as const;
type Tab = typeof TABS[number];

const txColor: Record<string, string> = {
  RESTOCK: 'bg-green-100 text-green-800',
  DEDUCTION: 'bg-red-100 text-red-800',
  ADJUSTMENT: 'bg-blue-100 text-blue-800',
  TRANSFER_OUT: 'bg-orange-100 text-orange-800',
  TRANSFER_IN: 'bg-purple-100 text-purple-800',
};

type PriceField = 'actualPrice' | 'markedPrice' | 'serviceCost' | 'marginCost';

interface ProductWithStock extends Product {
  currentQty: number;
  editQty: string;
  saving: boolean;
  priceSaving: boolean;
  editing: Partial<Record<PriceField, boolean>>;
  tempValues: Partial<Record<PriceField, string>>;
}

const InventoryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('Stock Levels');
  const [darkStores, setDarkStores] = useState<DarkStore[]>([]);
  const [selectedDarkStore, setSelectedDarkStore] = useState<number | ''>('');
  const [products, setProducts] = useState<ProductWithStock[]>([]);
  const [summary, setSummary] = useState<InventorySummaryItem[]>([]);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [makes, setMakes] = useState<Make[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [selectedMake, setSelectedMake] = useState<number | ''>('');
  const [selectedModel, setSelectedModel] = useState<number | ''>('');
  const [selectedServiceType, setSelectedServiceType] = useState<string>('');
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);
  const [transactions, setTransactions] = useState<InventoryTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  // Transfer form
  const [transfer, setTransfer] = useState({ fromDarkStoreId: '', toDarkStoreId: '', serviceId: '', quantity: '', notes: '' });
  const [transferSearch, setTransferSearch] = useState('');
  const [transferProducts, setTransferProducts] = useState<Product[]>([]);
  const [selectedTransferProduct, setSelectedTransferProduct] = useState<Product | null>(null);

  // Dark store form
  const [newStore, setNewStore] = useState({ name: '', address: '', city: 'Mumbai', contactPerson: '', phone: '' });
  const [editStore, setEditStore] = useState<DarkStore | null>(null);

  const userId = Number(localStorage.getItem('userId') || 1);

  useEffect(() => { loadDarkStores(); loadMakes(); loadServiceTypes(); }, []);

  useEffect(() => {
    if (activeTab === 'Stock Levels') {
      loadProducts();
      if (selectedDarkStore) loadSummary();
      else setSummary([]);
    }
  }, [activeTab, selectedDarkStore, selectedMake, selectedModel, selectedServiceType, currentPage, search]);

  useEffect(() => {
    if (activeTab === 'Transactions') loadTransactions();
  }, [activeTab]);

  // When make changes: reset model selection and reload models
  useEffect(() => {
    setSelectedModel('');
    setModels([]);
    if (selectedMake) {
      apiService.getModels(Number(selectedMake))
        .then(r => setModels(r.entity || r || []))
        .catch(() => {});
    }
    setCurrentPage(1);
  }, [selectedMake]);

  useEffect(() => { setCurrentPage(1); }, [search, selectedModel, selectedServiceType]);

  const loadMakes = async () => {
    try {
      const r = await apiService.getMakes();
      setMakes(r.entity || r || []);
    } catch {}
  };

  const loadServiceTypes = async () => {
    try {
      const types = await apiService.getProductServiceTypes();
      setServiceTypes(types);
    } catch {}
  };

  const loadSummary = async () => {
    if (!selectedDarkStore) return;
    try {
      const data = await apiService.getInventorySummary(
        Number(selectedDarkStore),
        selectedMake ? Number(selectedMake) : undefined,
        selectedModel ? Number(selectedModel) : undefined
      );
      setSummary(Array.isArray(data) ? data : []);
    } catch {
      setSummary([]);
    }
  };

  const loadDarkStores = async () => {
    try {
      const stores = await apiService.getDarkStores();
      if (Array.isArray(stores)) {
        setDarkStores(stores);
        if (stores.length > 0 && !selectedDarkStore) setSelectedDarkStore(stores[0].id);
      }
    } catch {
      // Tables may not exist yet
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const response = await apiService.getProducts(
        currentPage, pageSize, search,
        selectedMake ? Number(selectedMake) : undefined,
        selectedModel ? Number(selectedModel) : undefined,
        selectedServiceType || undefined
      );
      const productList: Product[] = response?.entity?.items || response?.entity || [];
      const total: number = response?.entity?.totalCount || 0;
      setTotalCount(total);

      let inventoryMap: Record<number, number> = {};
      if (selectedDarkStore) {
        try {
          const inv: InventoryItem[] = await apiService.getInventory({ darkStoreId: selectedDarkStore, pageSize: 500 });
          if (Array.isArray(inv)) inv.forEach(i => { inventoryMap[i.serviceId] = i.quantity; });
        } catch {
          // Inventory tables may not exist yet
        }
      }

      setProducts(productList.map(p => ({
        ...p,
        marginCost: (p as any).marginalCost ?? p.marginCost ?? 0,
        currentQty: inventoryMap[p.id] ?? 0,
        editQty: (inventoryMap[p.id] ?? 0).toString(),
        saving: false,
        priceSaving: false,
        editing: {},
        tempValues: {},
      })));
    } catch {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const result = await apiService.getInventoryTransactions({ pageSize: 100 });
      setTransactions(Array.isArray(result) ? result : []);
    } catch {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Inline quantity handlers ──────────────────────────────
  const updateEditQty = (productId: number, value: string) => {
    setProducts(prev => prev.map(p => p.id === productId ? { ...p, editQty: value } : p));
  };

  const quickAdjust = async (product: ProductWithStock, delta: number) => {
    if (!selectedDarkStore) return toast.error('Select a dark store first');
    await saveQty(product, Math.max(0, product.currentQty + delta));
  };

  const saveQty = async (product: ProductWithStock, newQty?: number) => {
    if (!selectedDarkStore) return toast.error('Select a dark store first');
    const qty = newQty !== undefined ? newQty : parseInt(product.editQty);
    if (isNaN(qty) || qty < 0) return toast.error('Invalid quantity');

    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, saving: true } : p));
    try {
      await apiService.adjustInventory({
        darkStoreId: Number(selectedDarkStore),
        serviceId: product.id,
        newQuantity: qty,
        notes: 'Manual adjustment from Inventory page',
        createdBy: userId,
      });
      setProducts(prev => prev.map(p =>
        p.id === product.id ? { ...p, currentQty: qty, editQty: qty.toString(), saving: false } : p
      ));
      toast.success(`Updated: ${product.serviceName}`);
    } catch {
      toast.error('Failed to update stock');
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, saving: false } : p));
    }
  };

  // ── Price editing (same pattern as ProductGridPage) ───────
  const startEditing = (productId: number, field: PriceField) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== productId) return p;
      const current = field === 'markedPrice'
        ? String(p.markedPrice)
        : field === 'marginCost'
        ? String(p.marginCost ?? (p as any).marginalCost ?? 0)
        : String(p[field] ?? 0);
      return { ...p, editing: { ...p.editing, [field]: true }, tempValues: { ...p.tempValues, [field]: current } };
    }));
  };

  const cancelEditing = (productId: number, field: PriceField) => {
    setProducts(prev => prev.map(p => {
      if (p.id !== productId) return p;
      const editing = { ...p.editing }; delete editing[field];
      const tempValues = { ...p.tempValues }; delete tempValues[field];
      return { ...p, editing, tempValues };
    }));
  };

  const updateTempValue = (productId: number, field: PriceField, value: string) => {
    setProducts(prev => prev.map(p =>
      p.id === productId ? { ...p, tempValues: { ...p.tempValues, [field]: value } } : p
    ));
  };

  const savePrice = async (product: ProductWithStock, field: PriceField) => {
    const raw = product.tempValues[field];
    if (raw === undefined) return;
    const num = parseFloat(raw);
    if (isNaN(num) || num < 0) { cancelEditing(product.id, field); return toast.error('Invalid value'); }

    const currentVal = field === 'markedPrice'
      ? parseFloat(String(product.markedPrice))
      : field === 'marginCost'
      ? (product.marginCost ?? (product as any).marginalCost ?? 0)
      : (product[field] as number) ?? 0;
    if (num === currentVal) { cancelEditing(product.id, field); return; }

    setProducts(prev => prev.map(p => p.id === product.id ? { ...p, priceSaving: true } : p));
    try {
      const form = new FormData();
      form.append('id', product.id.toString());
      form.append('makeId', product.makeId.toString());
      form.append('modelId', product.modelId.toString());
      form.append('modelName', product.modelName || '');
      form.append('serviceName', product.serviceName);
      form.append('actualPrice', field === 'actualPrice' ? num.toString() : product.actualPrice.toString());
      form.append('markedPrice', field === 'markedPrice' ? num.toString() : String(product.markedPrice));
      form.append('serviceDuration', product.serviceDuration.toString());
      form.append('serviceCost', field === 'serviceCost' ? num.toString() : product.serviceCost.toString());
      form.append('MarginalCost', field === 'marginCost' ? num.toString() : String(product.marginCost ?? (product as any).marginalCost ?? 0));

      const res = await apiService.updateProduct(product.id, form);
      if (res.isSuccess) {
        setProducts(prev => prev.map(p => {
          if (p.id !== product.id) return p;
          const editing = { ...p.editing }; delete editing[field];
          const tempValues = { ...p.tempValues }; delete tempValues[field];
          return {
            ...p,
            [field]: num,
            markedPrice: field === 'markedPrice' ? num.toString() : p.markedPrice,
            marginCost: field === 'marginCost' ? num : p.marginCost,
            priceSaving: false, editing, tempValues,
          };
        }));
        toast.success('Price updated');
      } else {
        toast.error(res.errors?.join(', ') || 'Failed to update');
        setProducts(prev => prev.map(p => p.id === product.id ? { ...p, priceSaving: false } : p));
        cancelEditing(product.id, field);
      }
    } catch {
      toast.error('Failed to update price');
      setProducts(prev => prev.map(p => p.id === product.id ? { ...p, priceSaving: false } : p));
      cancelEditing(product.id, field);
    }
  };

  const formatCurrency = (v: number | string) => {
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // ── Transfer ──────────────────────────────────────────────
  const searchTransferProducts = async () => {
    if (!transferSearch.trim()) return;
    try {
      const res = await apiService.getProducts(1, 10, transferSearch);
      setTransferProducts(res?.entity?.items || res?.entity || []);
    } catch { toast.error('Search failed'); }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transfer.fromDarkStoreId || !transfer.toDarkStoreId || !selectedTransferProduct || !transfer.quantity)
      return toast.error('Fill all required fields');
    if (transfer.fromDarkStoreId === transfer.toDarkStoreId)
      return toast.error('Source and destination must be different');
    try {
      await apiService.transferInventory({
        fromDarkStoreId: Number(transfer.fromDarkStoreId),
        toDarkStoreId: Number(transfer.toDarkStoreId),
        serviceId: selectedTransferProduct.id,
        quantity: Number(transfer.quantity),
        notes: transfer.notes,
        createdBy: userId,
      });
      toast.success('Transfer successful');
      setTransfer({ fromDarkStoreId: '', toDarkStoreId: '', serviceId: '', quantity: '', notes: '' });
      setSelectedTransferProduct(null);
      setTransferSearch('');
      setTransferProducts([]);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Transfer failed');
    }
  };

  // ── Dark Stores ───────────────────────────────────────────
  const handleCreateStore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStore.name) return toast.error('Name is required');
    try {
      await apiService.createDarkStore({ ...newStore, isActive: true });
      toast.success('Dark store created');
      setNewStore({ name: '', address: '', city: 'Mumbai', contactPerson: '', phone: '' });
      loadDarkStores();
    } catch { toast.error('Failed to create dark store'); }
  };

  const handleUpdateStore = async () => {
    if (!editStore) return;
    try {
      await apiService.updateDarkStore(editStore.id, editStore);
      toast.success('Updated');
      setEditStore(null);
      loadDarkStores();
    } catch { toast.error('Failed to update'); }
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const selectedStoreName = darkStores.find(d => d.id === selectedDarkStore)?.name || '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
          <Warehouse className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Inventory Management</h1>
          <p className="text-sm text-gray-500">Manage stock across dark stores</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'border-b-2 border-emerald-600 text-emerald-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── STOCK LEVELS TAB ──────────────────────────────────── */}
      {activeTab === 'Stock Levels' && (
        <div className="space-y-4">
          {/* Controls */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Dark Store</label>
                  <select
                    value={selectedDarkStore}
                    onChange={e => { setSelectedDarkStore(e.target.value ? Number(e.target.value) : ''); setCurrentPage(1); }}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium bg-white min-w-[180px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select Dark Store</option>
                    {darkStores.map(ds => <option key={ds.id} value={ds.id}>{ds.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Brand</label>
                  <SearchableSelect
                    options={makes}
                    value={selectedMake}
                    onChange={v => setSelectedMake(v)}
                    placeholder="All Brands"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Model</label>
                  <SearchableSelect
                    options={models}
                    value={selectedModel}
                    onChange={v => { setSelectedModel(v); setCurrentPage(1); }}
                    placeholder={selectedMake ? 'All Models' : 'Select brand first'}
                    disabled={!selectedMake}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Service Type</label>
                  <select
                    value={selectedServiceType}
                    onChange={e => { setSelectedServiceType(e.target.value); setCurrentPage(1); }}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white min-w-[160px] focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">All Services</option>
                    {serviceTypes.map(st => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 w-52"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">
                  Showing {products.length} of {totalCount} products
                </span>
                <button
                  onClick={loadProducts}
                  className="flex items-center gap-1.5 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
                >
                  <RefreshCw size={14} /> Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Summary by Service Type */}
          {summary.length > 0 && selectedDarkStore && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setSummaryOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    Stock Summary — {selectedStoreName}
                  </span>
                  <span className="text-xs text-gray-400 font-normal normal-case">
                    {summary.length} service types · {summary.reduce((a, s) => a + s.totalUnits, 0)} total units
                  </span>
                </div>
                {summaryOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
              </button>

              {summaryOpen && <div className="px-5 pb-5 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Service Type</th>
                      <th className="text-center pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Total SKUs</th>
                      <th className="text-center pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">In Stock</th>
                      <th className="text-center pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Low Stock</th>
                      <th className="text-center pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Out of Stock</th>
                      <th className="text-center pb-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Total Units</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {summary.map(s => (
                      <tr key={s.serviceType} className="hover:bg-gray-50">
                        <td className="py-2.5 font-medium text-gray-900">{s.serviceType}</td>
                        <td className="py-2.5 text-center text-gray-600">{s.totalSkus}</td>
                        <td className="py-2.5 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            {s.inStock}
                          </span>
                        </td>
                        <td className="py-2.5 text-center">
                          {s.lowStock > 0
                            ? <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">{s.lowStock}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-2.5 text-center">
                          {s.outOfStock > 0
                            ? <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{s.outOfStock}</span>
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-2.5 text-center font-semibold text-gray-900">{s.totalUnits}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200">
                    <tr>
                      <td className="pt-2 font-semibold text-gray-900">Total</td>
                      <td className="pt-2 text-center font-semibold text-gray-900">{summary.reduce((a, s) => a + s.totalSkus, 0)}</td>
                      <td className="pt-2 text-center font-semibold text-green-700">{summary.reduce((a, s) => a + s.inStock, 0)}</td>
                      <td className="pt-2 text-center font-semibold text-orange-700">{summary.reduce((a, s) => a + s.lowStock, 0)}</td>
                      <td className="pt-2 text-center font-semibold text-red-700">{summary.reduce((a, s) => a + s.outOfStock, 0)}</td>
                      <td className="pt-2 text-center font-semibold text-gray-900">{summary.reduce((a, s) => a + s.totalUnits, 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>}
            </div>
          )}

          {/* Product Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <Warehouse className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500">Try adjusting your search criteria.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Make / Model</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Marked Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Cost</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Margin Cost</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-52">
                          Quantity
                          {selectedDarkStore && (
                            <span className="ml-1 normal-case font-normal text-gray-400">({selectedStoreName})</span>
                          )}
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Stock Status</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-28">Visible</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map(product => {
                        const isActive = product.status !== 0;
                        const isLow = isActive && product.currentQty > 0 && product.currentQty <= 5;
                        const isOut = isActive && product.currentQty === 0;
                        const isDirty = parseInt(product.editQty) !== product.currentQty;
                        return (
                          <tr
                            key={product.id}
                            className={`hover:bg-gray-50 ${!isActive ? 'opacity-50 bg-gray-50' : isOut ? 'bg-red-50 hover:bg-red-50' : isLow ? 'bg-orange-50 hover:bg-orange-50' : ''}`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{product.make}</div>
                              <div className="text-xs text-gray-500">{product.modelName}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {product.serviceImage && (
                                  <img
                                    src={apiService.getFullImageUrl(product.serviceImage)}
                                    alt={product.serviceName}
                                    className="h-9 w-9 rounded-lg object-cover mr-3 flex-shrink-0"
                                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                )}
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{product.serviceName}</div>
                                  <div className="text-xs text-gray-500">ID: {product.id}</div>
                                </div>
                              </div>
                            </td>
                            {/* Parts Price */}
                            {(['actualPrice', 'markedPrice', 'serviceCost', 'marginCost'] as PriceField[]).map(field => (
                              <td key={field} className="px-6 py-4 whitespace-nowrap">
                                {product.editing[field] ? (
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number" min="0" step="1" autoFocus
                                      value={product.tempValues[field] ?? ''}
                                      onChange={e => updateTempValue(product.id, field, e.target.value)}
                                      onBlur={() => savePrice(product, field)}
                                      onKeyDown={e => { if (e.key === 'Enter') savePrice(product, field); if (e.key === 'Escape') cancelEditing(product.id, field); }}
                                      disabled={product.priceSaving}
                                      className="w-24 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                    <button onClick={() => savePrice(product, field)} disabled={product.priceSaving} className="text-green-600 hover:text-green-800 disabled:opacity-50">
                                      <Save size={14} />
                                    </button>
                                    <button onClick={() => cancelEditing(product.id, field)} disabled={product.priceSaving} className="text-red-500 hover:text-red-700 disabled:opacity-50">
                                      <X size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5 group/price">
                                    <span className="text-sm font-medium text-gray-900">
                                      {formatCurrency(
                                        field === 'markedPrice' ? product.markedPrice :
                                        field === 'marginCost' ? (product.marginCost ?? (product as any).marginalCost ?? 0) :
                                        product[field] as number
                                      )}
                                    </span>
                                    {product.priceSaving
                                      ? <div className="w-3.5 h-3.5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                      : <button onClick={() => startEditing(product.id, field)} className="text-purple-500 hover:text-purple-700 opacity-0 group-hover/price:opacity-100 transition-opacity">
                                          <Edit2 size={13} />
                                        </button>
                                    }
                                  </div>
                                )}
                              </td>
                            ))}
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  disabled={!selectedDarkStore || product.saving || product.currentQty <= 0}
                                  onClick={() => quickAdjust(product, -1)}
                                  className="w-7 h-7 rounded-md border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Minus size={12} />
                                </button>
                                <input
                                  type="number"
                                  min={0}
                                  value={product.editQty}
                                  onChange={e => updateEditQty(product.id, e.target.value)}
                                  disabled={!selectedDarkStore || product.saving}
                                  className={`w-16 text-center border rounded-md py-1 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500
                                    ${isDirty ? 'border-emerald-400 bg-emerald-50' : 'border-gray-300'}
                                    ${isOut ? 'text-red-600' : isLow ? 'text-orange-600' : 'text-gray-900'}
                                    disabled:bg-gray-50 disabled:cursor-not-allowed`}
                                  onKeyDown={e => { if (e.key === 'Enter') saveQty(product); }}
                                />
                                <button
                                  disabled={!selectedDarkStore || product.saving}
                                  onClick={() => quickAdjust(product, 1)}
                                  className="w-7 h-7 rounded-md border border-gray-300 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Plus size={12} />
                                </button>
                                {isDirty && !product.saving && (
                                  <button
                                    onClick={() => saveQty(product)}
                                    className="w-7 h-7 rounded-md bg-emerald-600 flex items-center justify-center text-white hover:bg-emerald-700"
                                    title="Save"
                                  >
                                    <Save size={12} />
                                  </button>
                                )}
                                {product.saving && (
                                  <div className="w-7 h-7 flex items-center justify-center">
                                    <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                  </div>
                                )}
                              </div>
                            </td>
                            {/* Stock status */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              {!isActive
                                ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Disabled</span>
                                : isOut
                                ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Out</span>
                                : isLow
                                ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">Low</span>
                                : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">OK</span>
                              }
                            </td>

                            {/* Visible to customer toggle */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <button
                                onClick={async () => {
                                  const newStatus = isActive ? 0 : 1;
                                  try {
                                    await apiService.updateServiceStatus(product.id, newStatus);
                                    setProducts(prev => prev.map(p =>
                                      p.id === product.id ? { ...p, status: newStatus } : p
                                    ));
                                    toast.success(newStatus === 1 ? 'Service enabled' : 'Service disabled');
                                  } catch {
                                    toast.error('Failed to update visibility');
                                  }
                                }}
                                title={isActive ? 'Click to disable (hide from customers)' : 'Click to enable (show to customers)'}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${
                                  isActive ? 'bg-emerald-500' : 'bg-gray-300'
                                }`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                  isActive ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 bg-white">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >Previous</button>
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = Math.max(1, currentPage - 2) + i;
                          if (page > totalPages) return null;
                          return (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 text-sm font-medium rounded-md ${
                                page === currentPage
                                  ? 'bg-emerald-600 text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >{page}</button>
                          );
                        })}
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >Next</button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── TRANSFER TAB ──────────────────────────────────────── */}
      {activeTab === 'Transfer' && (
        <div className="max-w-lg">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-5">
              <ArrowRightLeft size={18} className="text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">Transfer Stock Between Dark Stores</h2>
            </div>
            <form onSubmit={handleTransfer} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">From *</label>
                  <select
                    value={transfer.fromDarkStoreId}
                    onChange={e => setTransfer(r => ({ ...r, fromDarkStoreId: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Select source</option>
                    {darkStores.filter(ds => ds.isActive).map(ds => <option key={ds.id} value={ds.id}>{ds.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">To *</label>
                  <select
                    value={transfer.toDarkStoreId}
                    onChange={e => setTransfer(r => ({ ...r, toDarkStoreId: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  >
                    <option value="">Select destination</option>
                    {darkStores.filter(ds => ds.isActive && String(ds.id) !== transfer.fromDarkStoreId).map(ds => (
                      <option key={ds.id} value={ds.id}>{ds.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Product *</label>
                {selectedTransferProduct ? (
                  <div className="flex items-center justify-between border border-green-300 bg-green-50 rounded-lg px-3 py-2.5">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{selectedTransferProduct.serviceName}</div>
                      <div className="text-xs text-gray-500">{selectedTransferProduct.make} · {selectedTransferProduct.modelName}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setSelectedTransferProduct(null); setTransferProducts([]); setTransferSearch(''); }}
                      className="text-gray-400 hover:text-gray-600 text-xs"
                    >✕ Change</button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Search product by name..."
                        value={transferSearch}
                        onChange={e => setTransferSearch(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchTransferProducts(); } }}
                        className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                      <button type="button" onClick={searchTransferProducts}
                        className="bg-gray-100 border border-gray-200 rounded-lg px-3 py-2 text-sm hover:bg-gray-200">
                        Search
                      </button>
                    </div>
                    {transferProducts.length > 0 && (
                      <div className="border border-gray-200 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                        {transferProducts.map(p => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => { setSelectedTransferProduct(p); setTransferProducts([]); }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-gray-100 last:border-0"
                          >
                            <div className="text-sm font-medium text-gray-900">{p.serviceName}</div>
                            <div className="text-xs text-gray-400">{p.make} · {p.modelName} · ID: {p.id}</div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantity *</label>
                <input
                  type="number" min={1} placeholder="Enter quantity"
                  value={transfer.quantity}
                  onChange={e => setTransfer(r => ({ ...r, quantity: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes</label>
                <textarea
                  placeholder="Reason for transfer"
                  value={transfer.notes}
                  onChange={e => setTransfer(r => ({ ...r, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  rows={2}
                />
              </div>
              <button type="submit" className="w-full bg-purple-600 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-purple-700 transition-colors">
                Transfer Stock
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── TRANSACTIONS TAB ──────────────────────────────────── */}
      {activeTab === 'Transactions' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-400">No transactions yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dark Store</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Before → After</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map(tx => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">
                        {new Date(tx.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${txColor[tx.transactionType] || 'bg-gray-100 text-gray-700'}`}>
                          {tx.transactionType.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tx.serviceName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{tx.darkStoreName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-semibold">
                        <span className={tx.quantity < 0 ? 'text-red-600' : 'text-green-600'}>
                          {tx.quantity > 0 ? '+' : ''}{tx.quantity}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-xs text-gray-500">
                        {tx.quantityBefore} → {tx.quantityAfter}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-400">
                        {tx.referenceType === 'order' ? `Order #${tx.referenceId}` : tx.referenceType || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── DARK STORES TAB ───────────────────────────────────── */}
      {activeTab === 'Dark Stores' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 max-w-lg">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Dark Store</h2>
            <form onSubmit={handleCreateStore} className="space-y-3">
              <input type="text" placeholder="Name *" value={newStore.name}
                onChange={e => setNewStore(s => ({ ...s, name: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" required />
              <input type="text" placeholder="Address" value={newStore.address}
                onChange={e => setNewStore(s => ({ ...s, address: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <input type="text" placeholder="City" value={newStore.city}
                onChange={e => setNewStore(s => ({ ...s, city: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <input type="text" placeholder="Contact Person" value={newStore.contactPerson}
                onChange={e => setNewStore(s => ({ ...s, contactPerson: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <input type="text" placeholder="Phone" value={newStore.phone}
                onChange={e => setNewStore(s => ({ ...s, phone: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
              <button type="submit" className="w-full bg-emerald-600 text-white py-2.5 rounded-lg font-medium text-sm hover:bg-emerald-700 transition-colors">
                Create Dark Store
              </button>
            </form>
          </div>

          <div className="space-y-3 max-w-lg">
            {darkStores.map(ds => (
              <div key={ds.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                {editStore?.id === ds.id ? (
                  <div className="grid grid-cols-2 gap-2">
                    <input value={editStore.name} onChange={e => setEditStore(s => s ? { ...s, name: e.target.value } : s)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm col-span-2 focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Name" />
                    <input value={editStore.address || ''} onChange={e => setEditStore(s => s ? { ...s, address: e.target.value } : s)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Address" />
                    <input value={editStore.city || ''} onChange={e => setEditStore(s => s ? { ...s, city: e.target.value } : s)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="City" />
                    <input value={editStore.contactPerson || ''} onChange={e => setEditStore(s => s ? { ...s, contactPerson: e.target.value } : s)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Contact" />
                    <input value={editStore.phone || ''} onChange={e => setEditStore(s => s ? { ...s, phone: e.target.value } : s)}
                      className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" placeholder="Phone" />
                    <div className="col-span-2 flex gap-2 mt-1">
                      <button onClick={handleUpdateStore} className="bg-emerald-600 text-white px-4 py-1.5 rounded-lg text-sm hover:bg-emerald-700">Save</button>
                      <button onClick={() => setEditStore(null)} className="bg-gray-100 text-gray-700 px-4 py-1.5 rounded-lg text-sm hover:bg-gray-200">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-semibold text-gray-900 flex items-center gap-2">
                        {ds.name}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ds.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {ds.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">{[ds.address, ds.city].filter(Boolean).join(' · ')}</div>
                      {ds.contactPerson && <div className="text-xs text-gray-400 mt-0.5">Contact: {ds.contactPerson}{ds.phone ? ` · ${ds.phone}` : ''}</div>}
                    </div>
                    <button onClick={() => setEditStore(ds)} className="text-sm text-emerald-600 hover:text-emerald-800 font-medium">Edit</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
